// dashboard/src/pages/apps-setup/BuildTab.tsx
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shortBuildId } from '@/lib/build-id';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '../../components/Select';
import { BuildQaControl } from '../../components/BuildQaControl';
import { AabCheckPanel, AabStateIcon, AAB_BUTTON, aabReasons } from './AabCheckPanel';
import { AppDomainPanel } from './AppDomainPanel';
import { KeystoreFingerprint } from './KeystoreFingerprint';
import { UnityVersionPanel } from './UnityVersionPanel';
import { RandomSeedPanel } from './RandomSeedPanel';
import { LoaderPanel } from './LoaderPanel';
import { getApps } from '../../api/apps';
import { ApiError } from '../../api/client';
import {
  getBuildRepos, getRepoTypes,
  getBuildConfig, saveBuildConfig, generateKeystore, getSigning,
  downloadKeystore, startBuild, cancelBuild, getBuilds, downloadArtifact, downloadObfsMapping,
  getBuildRepoHistory, getPlayConsoleVersion, getAabCheck,
  getUnityVersions, unityLineLabel, REPO_ITEM_LABELS,
  type AppBuild, type ReadinessItem,
} from '../../api/app-builds';

const STATUS_STYLES: Record<string, string> = {
  dispatched: 'bg-yellow-500/15 text-yellow-500',
  running: 'bg-blue-500/15 text-blue-400',
  publishing: 'bg-purple-500/15 text-purple-400',
  success: 'bg-green-500/15 text-green-400',
  published: 'bg-green-500/15 text-green-400',
  failed: 'bg-red-500/15 text-red-400',
  cancelled: 'bg-surface-muted text-content-subtle',
};
const ACTIVE = new Set(['dispatched', 'running', 'uploaded', 'publishing']);
const RECENT_BUILDS_PAGE_SIZE = 5;

// What the build needs whatever the repo type; the type's own items come as readiness.items.
const MISSING_LABELS: Record<string, string> = {
  bundle_id: 'Bundle ID (Create tab)',
  icon: 'App icon',
  base_repo: 'Base repository',
  repo_type: 'The base repository has no type. Set it on the Repos Setup tab.',
  keystore: 'Keystore',
};
const TYPE_ITEM_KEYS = new Set(['google_services', 'admob', 'loader_icon', 'loader_text', 'loader_bg', 'unity_version', 'random_seed', 'product_guid', 'domain']);

// Where an item the app lacks is filled in.
const ITEM_WHERE: Partial<Record<ReadinessItem['key'], string>> = {
  firebase: 'create Firebase on the Apps row, or the Connect tab',
  admob: 'the Connect tab',
  loader_icon: 'the Loader block below',
  loader_text: 'the Loader block below',
  loader_bg: 'the Loader block below',
  random_seed: 'the Random seed block below',
};

function ReadinessRow({ item, typeName }: { item: ReadinessItem; typeName: string }) {
  const name = REPO_ITEM_LABELS[item.key].name;
  const [mark, cls, text] = item.state === 'set'
    ? ['✓', 'text-green-500', `${name}: set${item.rule === 'opt' ? ' (optional)' : ''}${item.note ? ` · ${item.note}` : ''}`]
    : item.state === 'missing'
      ? ['✗', 'text-red-500', `${name}: ${item.note ?? `required for ${typeName}, not set for this app`}${!item.note && ITEM_WHERE[item.key] ? ` — ${ITEM_WHERE[item.key]}` : ''}`]
      : item.state === 'later'
        ? ['✓', 'text-green-500', `${name}: ${item.note}`]
        : ['○', 'text-content-subtle', `${name}: ${item.note ?? 'not set'}. Optional, the build goes without it.`];
  return <li className={`text-sm ${cls}`}><span className="inline-block w-4">{mark}</span>{text}</li>;
}

function fmtUnix(s: number): string {
  return new Date(s * 1000).toLocaleString();
}

function fmtUnixCompact(s: number): string {
  return new Date(s * 1000).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function StatusChip({ b }: { b: AppBuild }) {
  return (
    <span
      title={b.error ?? undefined}
      className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${STATUS_STYLES[b.status] ?? 'bg-surface-muted text-content-subtle'}`}
    >
      {b.status}
    </span>
  );
}

// The short build id sits next to the version in every build list, so a
// build reported elsewhere (Update Batches) can be matched here by eye.
function BuildIdLabel({ id }: { id: string }) {
  return <span className="font-mono text-content-subtle" title={`Build ${id}`}>#{shortBuildId(id)}</span>;
}

// Stops a build still in CI — e.g. when its APK/AAB twin already failed the
// same tests and this one is bound to fail too.
function StopBuildButton({ b }: { b: AppBuild }) {
  const qc = useQueryClient();
  const stop = useMutation({
    mutationFn: () => cancelBuild(b.id),
    onSuccess: (r) => { if (r.warning) window.alert(r.warning); },
    onError: (e) => window.alert(e instanceof Error ? e.message : 'Stop failed'),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['app-builds'] });
      qc.invalidateQueries({ queryKey: ['app-builds-recent'] });
    },
  });
  if (b.status !== 'dispatched' && b.status !== 'running') return null;
  return (
    <button
      type="button"
      disabled={stop.isPending}
      onClick={() => {
        if (window.confirm(`Stop the ${b.build_type.toUpperCase()} build v${b.version_name} (${b.version_code})?`)) stop.mutate();
      }}
      className="text-xs text-red-500 hover:underline disabled:opacity-50"
    >
      {stop.isPending ? 'Stopping…' : 'Stop'}
    </button>
  );
}

function BuildsTable({ builds, onQaSaved }: { builds: AppBuild[]; onQaSaved: () => void }) {
  const [downloadError, setDownloadError] = useState('');
  // ?build=<id> (Update Batches → APK / QA / AAB cells) points at one row.
  const [searchParams] = useSearchParams();
  const wanted = searchParams.get('build');
  const unityQ = useQuery({ queryKey: ['unity-versions'], queryFn: getUnityVersions, staleTime: 60_000 });
  const unityByVersion = useMemo(() => new Map((unityQ.data ?? []).map((v) => [v.version, v])), [unityQ.data]);

  async function handleDownload(b: AppBuild) {
    setDownloadError('');
    try {
      await downloadArtifact(b);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  }

  async function handleDownloadMapping(b: AppBuild) {
    setDownloadError('');
    try {
      await downloadObfsMapping(b);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  }

  if (builds.length === 0) {
    return <p className="text-content-subtle text-sm">No builds yet.</p>;
  }
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-content-subtle text-xs uppercase tracking-wide text-left">
              <th className="py-1.5 pr-3 font-medium">App</th>
              <th className="py-1.5 pr-3 font-medium">Type</th>
              <th className="py-1.5 pr-3 font-medium">Version</th>
              <th className="py-1.5 pr-3 font-medium">Unity</th>
              <th className="py-1.5 pr-3 font-medium">Base repo</th>
              <th className="py-1.5 pr-3 font-medium">Status</th>
              <th className="py-1.5 pr-3 font-medium">Проверка QA</th>
              <th className="py-1.5 pr-3 font-medium">Date</th>
              <th className="py-1.5 pr-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {builds.map((b) => (
              <tr key={b.id} className={`border-t border-border/50 align-top ${b.id === wanted ? 'bg-primary/10' : ''}`}>
                <td className="py-1.5 pr-3 min-w-0">
                  <div className="text-content truncate">{b.app_name || b.app_id}</div>
                  {b.bundle_id && <div className="text-content-subtle text-xs truncate">{b.bundle_id}</div>}
                </td>
                <td className="py-1.5 pr-3 uppercase text-content-muted">{b.build_type}</td>
                <td className="py-1.5 pr-3 text-content-muted whitespace-nowrap">
                  <div>{b.version_name} ({b.version_code})</div>
                  <div className="text-xs"><BuildIdLabel id={b.id} /></div>
                  {b.random_seed != null && <div className="text-content-subtle text-xs">seed <span className="select-all">{b.random_seed}</span></div>}
                </td>
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  {(() => {
                    // Null: the repo type sends no Unity version (builds before
                    // migration 229 were backfilled, so they always have one).
                    const v = b.unity_version;
                    if (!v) return <span className="text-content-subtle">—</span>;
                    const row = unityByVersion.get(v);
                    return (
                      <>
                        <div className="text-content-muted">{v}</div>
                        {row && <div className="text-content-subtle text-xs">{unityLineLabel(row)}</div>}
                      </>
                    );
                  })()}
                </td>
                <td className="py-1.5 pr-3 min-w-[10rem]">
                  {b.base_repo_name ? (
                    <>
                      <div className="text-content-muted truncate">{b.base_repo_name}</div>
                      {b.base_repo_owner && b.base_repo_repo && (
                        <div className="text-content-subtle text-xs truncate">
                          {b.base_repo_owner}/{b.base_repo_repo}{b.base_repo_branch ? `@${b.base_repo_branch}` : ''}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-content-subtle">Unknown (legacy build)</span>
                  )}
                </td>
                <td className="py-1.5 pr-3"><StatusChip b={b} /></td>
                <td className="py-1.5 pr-3">
                  <BuildQaControl
                    buildId={b.id}
                    status={b.qa_status}
                    note={b.qa_note ?? null}
                    reviewer={b.qa_by_name ?? null}
                    reviewedAt={b.qa_at ?? null}
                    onSaved={onQaSaved}
                  />
                </td>
                <td className="py-1.5 pr-3 text-content-subtle whitespace-nowrap">{fmtUnix(b.created_at)}</td>
                <td className="py-1.5 pr-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {b.artifact_r2_key && (
                      <button
                        type="button"
                        onClick={() => handleDownload(b)}
                        className="text-primary hover:underline text-xs"
                      >
                        Download
                      </button>
                    )}
                    {b.artifact_r2_key && b.gh_run_url && (
                      <button
                        type="button"
                        onClick={() => handleDownloadMapping(b)}
                        className="text-primary hover:underline text-xs"
                        title="Obfuscation decode map from the CI run"
                      >
                        ObfsDecodeMapping.json
                      </button>
                    )}
                    {b.gh_run_url && (
                      <a href={b.gh_run_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">
                        Logs
                      </a>
                    )}
                    <StopBuildButton b={b} />
                    {b.error && (
                      <span
                        title={b.error}
                        className={`text-xs truncate max-w-[16rem] inline-block align-bottom ${b.status === 'failed' ? 'text-red-500' : 'text-yellow-500'}`}
                      >
                        {b.error}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {downloadError && <p className="text-red-500 text-sm">{downloadError}</p>}
    </div>
  );
}

function RecentBuildsPanel({
  builds,
  loading,
  page,
  pageCount,
  onPrevious,
  onNext,
  onQaSaved,
}: {
  builds: AppBuild[];
  loading: boolean;
  page: number;
  pageCount: number;
  onPrevious: () => void;
  onNext: () => void;
  onQaSaved: () => void;
}) {
  const [downloadError, setDownloadError] = useState('');

  async function handleDownload(build: AppBuild) {
    setDownloadError('');
    try {
      await downloadArtifact(build);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  }

  async function handleDownloadMapping(build: AppBuild) {
    setDownloadError('');
    try {
      await downloadObfsMapping(build);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  }

  return (
    <section className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="font-medium text-content">Recent builds</h2>
        <p className="mt-0.5 text-xs text-content-subtle">Latest factory activity</p>
      </div>

      {loading ? (
        <p className="px-4 py-6 text-sm text-content-subtle">Loading…</p>
      ) : builds.length === 0 ? (
        <p className="px-4 py-6 text-sm text-content-subtle">No builds yet.</p>
      ) : (
        <div className="divide-y divide-border/70">
          {builds.map((build) => (
            <article key={build.id} className="px-4 py-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-content" title={build.app_name || build.app_id}>
                    {build.app_name || build.app_id}
                  </p>
                  {build.bundle_id && (
                    <p className="mt-0.5 truncate text-xs text-content-muted" title={build.bundle_id}>
                      {build.bundle_id}
                    </p>
                  )}
                  <p className="mt-0.5 text-xs text-content-subtle">
                    <span className="uppercase">{build.build_type}</span>
                    {' · '}v{build.version_name} ({build.version_code})
                    {' · '}{fmtUnixCompact(build.created_at)}
                    {' · '}<BuildIdLabel id={build.id} />
                  </p>
                  {build.requested_by && (
                    <p className="mt-0.5 truncate text-xs text-content-muted" title={build.requested_by_email ?? build.requested_by}>
                      by {build.requested_by_name?.trim() || build.requested_by_email || build.requested_by}
                    </p>
                  )}
                </div>
                <StatusChip b={build} />
              </div>

              {build.base_repo_name && (
                <p className="truncate text-xs text-content-muted" title={build.base_repo_name}>
                  {build.base_repo_name}
                  {build.base_repo_branch ? ` @ ${build.base_repo_branch}` : ''}
                </p>
              )}

              <BuildQaControl
                buildId={build.id}
                status={build.qa_status}
                note={build.qa_note ?? null}
                reviewer={build.qa_by_name ?? null}
                reviewedAt={build.qa_at ?? null}
                onSaved={onQaSaved}
              />

              <div className="flex items-center gap-3 min-w-0">
                {build.artifact_r2_key && (
                  <button
                    type="button"
                    onClick={() => handleDownload(build)}
                    className="text-xs text-primary hover:underline"
                  >
                    Download
                  </button>
                )}
                {build.artifact_r2_key && build.gh_run_url && (
                  <button
                    type="button"
                    onClick={() => handleDownloadMapping(build)}
                    className="text-xs text-primary hover:underline"
                    title="Obfuscation decode map from the CI run"
                  >
                    ObfsDecodeMapping.json
                  </button>
                )}
                {build.gh_run_url && (
                  <a href={build.gh_run_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                    Logs
                  </a>
                )}
                <StopBuildButton b={build} />
                {build.error && (
                  <span
                    className={`min-w-0 truncate text-xs ${build.status === 'failed' ? 'text-red-500' : 'text-yellow-500'}`}
                    title={build.error}
                  >
                    {build.error}
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {(pageCount > 1 || downloadError) && (
        <div className="px-4 py-3 border-t border-border space-y-2">
          {downloadError && <p className="text-xs text-red-500">{downloadError}</p>}
          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-content-subtle">{page} / {pageCount}</span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={onPrevious}
                  disabled={page === 1}
                  aria-label="Previous builds page"
                  className="px-2.5 py-1 text-xs rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={page === pageCount}
                  aria-label="Next builds page"
                  className="px-2.5 py-1 text-xs rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Exported so First publication can offer the same panel for the app it is
// about to publish, instead of a second, thinner build form.
export function AppBuildPanel({ appId, onOpenShare, onOpenRepos }: { appId: string; onOpenShare: () => void; onOpenRepos?: () => void }) {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ['build-config', appId], queryFn: () => getBuildConfig(appId) });
  const buildsQ = useQuery({
    queryKey: ['app-builds', appId],
    queryFn: () => getBuilds(appId),
    refetchInterval: (q) => ((q.state.data ?? []) as AppBuild[]).some((b) => ACTIVE.has(b.status)) ? 10_000 : false,
  });
  const reposQ = useQuery({ queryKey: ['build-repos'], queryFn: getBuildRepos });
  const typesQ = useQuery({ queryKey: ['repo-types'], queryFn: getRepoTypes, staleTime: 60_000 });
  const typeName = (id: number | null) => (typesQ.data ?? []).find((t) => t.id === id)?.name ?? null;
  const repoHistoryQ = useQuery({
    queryKey: ['build-repo-history', appId],
    queryFn: () => getBuildRepoHistory(appId),
  });
  const consoleVersionQ = useQuery({
    queryKey: ['play-console-version', appId],
    queryFn: () => getPlayConsoleVersion(appId),
    retry: false,
    staleTime: 60_000,
  });

  const [baseRepoId, setBaseRepoId] = useState('');
  const [versionName, setVersionName] = useState('');
  const [versionCode, setVersionCode] = useState('');
  const [versionScheme, setVersionScheme] = useState('');
  const [codeStepMax, setCodeStepMax] = useState('');
  // The server's version fields as last seen, to follow server-side changes
  // (a build spends codes; the first build draws the style) into the inputs
  // the user has not touched.
  const [serverVersion, setServerVersion] = useState<{ name: string; code: number; scheme: string; step: string } | null>(null);
  const [keyalias, setKeyalias] = useState('');
  const [keystorePass, setKeystorePass] = useState('');
  const [keyaliasPass, setKeyaliasPass] = useState('');
  const [keystoreFile, setKeystoreFile] = useState<File | null>(null);
  const [signing, setSigning] = useState<{ keyalias: string | null; keystore_pass: string; keyalias_pass: string | null } | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [ksDownloadError, setKsDownloadError] = useState('');
  const [showReupload, setShowReupload] = useState(false);

  const cfg = cfgQ.data?.config;
  const readiness = cfgQ.data?.readiness;
  const ads = cfgQ.data?.ads;
  const repoType = cfgQ.data?.repo_type ?? null;
  const enabledRepos = useMemo(
    () => (reposQ.data ?? []).filter((repo) => repo.enabled),
    [reposQ.data],
  );
  const latestRepoBuild = useMemo(
    () => (buildsQ.data ?? []).find((build) => build.base_repo_id != null),
    [buildsQ.data],
  );
  // Keyed on the newest APK and its verdict, so a new APK or a QA verdict
  // saved anywhere on this page re-runs the check; verdicts on other bundles
  // of the repo and repo commits are picked up on focus / after a minute.
  const latestApk = (buildsQ.data ?? []).find((b) => b.build_type === 'apk' && b.status === 'success');
  const aabCheckQ = useQuery({
    queryKey: ['aab-check', appId, latestApk?.id, latestApk?.qa_status, latestApk?.qa_at, cfg?.base_repo_id],
    queryFn: () => getAabCheck(appId),
    enabled: buildsQ.isSuccess,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!cfg || !buildsQ.isSuccess || !reposQ.isSuccess || seeded) return;
    const lastRepoIsAvailable = latestRepoBuild?.base_repo_id != null
      && enabledRepos.some((repo) => repo.id === latestRepoBuild.base_repo_id);
    setBaseRepoId(lastRepoIsAvailable ? String(latestRepoBuild.base_repo_id) : (cfg.base_repo_id ? String(cfg.base_repo_id) : ''));
    setVersionName(cfg.version_name || '');
    setVersionCode(String(cfg.next_version_code ?? ''));
    setVersionScheme(cfg.version_scheme ?? '');
    setCodeStepMax(cfg.version_code_step != null ? String(cfg.version_code_step) : '');
    setKeyalias(cfg.keyalias || '');
    setSeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, buildsQ.isSuccess, reposQ.isSuccess, latestRepoBuild, enabledRepos, seeded]);

  const saveCfg = useMutation({
    mutationFn: ({ fields, file }: { fields: Parameters<typeof saveBuildConfig>[1]; file?: File }) =>
      saveBuildConfig(appId, fields, file),
    onSuccess: (data, variables) => {
      // Waiting for approval: nothing was saved, keep the staged file and
      // passwords so the same Upload can be pressed again once approved.
      if (data.pending_approval) {
        setShowReupload(true);
        return;
      }
      qc.invalidateQueries({ queryKey: ['build-config', appId] });
      // Only clear the keystore-upload inputs when this save actually carried
      // a keystore file — a config-only save (repo/version fields) shouldn't
      // wipe whatever the user has staged in the upload form.
      if (variables.file) {
        qc.invalidateQueries({ queryKey: ['keystore-fingerprint', appId] });
        setKeystoreFile(null);
        setKeystorePass('');
        setKeyaliasPass('');
      }
    },
  });

  const genKs = useMutation({
    mutationFn: (force: boolean) => generateKeystore(appId, force),
    onSuccess: (data) => {
      if (data.pending_approval) return; // the toast says why nothing changed
      qc.invalidateQueries({ queryKey: ['build-config', appId] });
      qc.invalidateQueries({ queryKey: ['keystore-fingerprint', appId] });
      // A regenerated keystore invalidates any previously revealed passwords.
      setSigning(null);
    },
  });

  const showSigning = useMutation({
    mutationFn: () => getSigning(appId),
    onSuccess: (d) => setSigning(d),
  });

  const start = useMutation({
    mutationFn: (build_type: 'apk' | 'aab') => startBuild(appId, build_type),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['app-builds', appId] });
      qc.invalidateQueries({ queryKey: ['app-builds-recent'] });
      qc.invalidateQueries({ queryKey: ['build-repo-history', appId] });
      // The Unity block counts builds on the app's version; a first build may also draw it.
      qc.invalidateQueries({ queryKey: ['build-config', appId] });
    },
    // The first build buys the app's domain, and a refused start may have
    // got as far as registering it.
    onSettled: () => qc.invalidateQueries({ queryKey: ['app-domain', appId] }),
  });

  function saveGeneral() {
    saveCfg.mutate({
      fields: {
        base_repo_id: baseRepoId ? Number(baseRepoId) : undefined,
        version_name: versionName.trim() || undefined,
        next_version_code: versionCode ? Number(versionCode) : undefined,
        version_scheme: versionScheme || undefined,
        version_code_step: codeStepMax ? Number(codeStepMax) : undefined,
      },
    });
  }

  function selectBaseRepo(nextId: string) {
    if (nextId && latestRepoBuild?.base_repo_id != null && Number(nextId) !== latestRepoBuild.base_repo_id) {
      const nextRepo = enabledRepos.find((repo) => repo.id === Number(nextId));
      const previous = latestRepoBuild.base_repo_name
        ?? `${latestRepoBuild.base_repo_owner ?? ''}/${latestRepoBuild.base_repo_repo ?? ''}`;
      window.alert(
        `Warning: the last build of this app used “${previous}”. You selected “${nextRepo?.name ?? 'another repository'}”.`,
      );
    }
    setBaseRepoId(nextId);
  }

  function uploadKeystore() {
    if (!keystoreFile) return;
    saveCfg.mutate({
      fields: {
        keyalias: keyalias.trim() || undefined,
        keystore_pass: keystorePass || undefined,
        keyalias_pass: keyaliasPass || undefined,
      },
      file: keystoreFile,
    });
  }

  function reuploadKeystore() {
    if (!keystoreFile) return;
    if (!window.confirm('Upload your own keystore? The current signing key will be REPLACED — updates of already published builds will no longer install over old versions.')) return;
    uploadKeystore();
    setShowReupload(false);
  }

  async function handleDownloadKeystore() {
    setKsDownloadError('');
    try {
      await downloadKeystore(appId);
    } catch (e) {
      setKsDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  }

  function regenerate() {
    if (!window.confirm('Regenerate the keystore? The current signing key will be REPLACED — updates of already published builds will no longer install over old versions.')) return;
    if (!window.confirm('This cannot be undone. Really regenerate the keystore?')) return;
    genKs.mutate(true);
  }

  useEffect(() => {
    if (!cfg || !seeded) return;
    const now = {
      name: cfg.version_name, code: cfg.next_version_code,
      scheme: cfg.version_scheme ?? '', step: cfg.version_code_step != null ? String(cfg.version_code_step) : '',
    };
    const prev = serverVersion;
    if (prev) {
      if (prev.name !== now.name && versionName.trim() === prev.name) setVersionName(now.name);
      if (prev.code !== now.code && Number(versionCode) === prev.code) setVersionCode(String(now.code));
      if (prev.scheme !== now.scheme && versionScheme === prev.scheme) setVersionScheme(now.scheme);
      if (prev.step !== now.step && codeStepMax === prev.step) setCodeStepMax(now.step);
    }
    setServerVersion(now);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.version_name, cfg?.next_version_code, cfg?.version_scheme, cfg?.version_code_step, seeded]);

  const selectedRepoId = baseRepoId ? Number(baseRepoId) : null;
  const repoMismatch = selectedRepoId != null
    && latestRepoBuild?.base_repo_id != null
    && selectedRepoId !== latestRepoBuild.base_repo_id;
  const repoNeedsSave = selectedRepoId !== (cfg?.base_repo_id ?? null);
  const versionNeedsSave = seeded && (
    versionName.trim() !== (cfg?.version_name ?? '')
    || versionCode.trim() === ''
    || Number(versionCode) !== cfg?.next_version_code
    || versionScheme !== (cfg?.version_scheme ?? '')
    || codeStepMax !== (cfg?.version_code_step != null ? String(cfg.version_code_step) : '')
  );
  const configNeedsSave = repoNeedsSave || versionNeedsSave;
  const consoleVersion = consoleVersionQ.data;
  const configuredVersionCode = Number(versionCode);
  const consoleCodeConflict = consoleVersion?.connected
    && consoleVersion.next_version_code != null
    && versionCode.trim() !== ''
    && Number.isFinite(configuredVersionCode)
    && configuredVersionCode < consoleVersion.next_version_code;

  return (
    <div className="space-y-4">
      {cfgQ.isLoading ? (
        <p className="text-content-subtle text-sm">Loading…</p>
      ) : (
        <>
          {/* Readiness */}
          <section className="bg-surface border border-border rounded-lg p-4">
            <h3 className="font-medium text-content mb-2">Build readiness</h3>
            {readiness?.ok ? (
              <p className="text-green-500 text-sm mb-2">All set — ready to build.</p>
            ) : (
              <p className="text-red-500 text-sm mb-2">Not ready. Fill the required items below.</p>
            )}
            <ul className="space-y-1">
              {(readiness?.missing ?? []).filter((m) => !TYPE_ITEM_KEYS.has(m)).map((m) => (
                <li key={m} className="text-red-500 text-sm">
                  <span className="inline-block w-4">✗</span>{MISSING_LABELS[m] ?? m}
                  {m === 'repo_type' && onOpenRepos && (
                    <button type="button" onClick={onOpenRepos} className="ml-2 text-primary hover:underline">Open Repos Setup</button>
                  )}
                </li>
              ))}
              {repoType && (readiness?.items ?? []).filter((it) => it.state !== 'off').map((it) => (
                <ReadinessRow key={it.key} item={it} typeName={repoType.name} />
              ))}
              {repoType && (readiness?.items ?? []).some((it) => it.state === 'off') && (
                <li className="text-sm text-content-subtle">
                  <span className="inline-block w-4">–</span>
                  Not sent for {repoType.name}: {(readiness?.items ?? []).filter((it) => it.state === 'off').map((it) => REPO_ITEM_LABELS[it.key].name).join(', ')}
                </li>
              )}
            </ul>
            {ads?.enabled && (
              <p className="text-xs text-content-subtle mt-2">AdMob app: <code>{ads.admob_app_id}</code></p>
            )}
          </section>

          {/* Config */}
          <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
            <h3 className="font-medium text-content">Configuration</h3>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-surface-muted px-3 py-2 text-xs">
              <span className="font-medium text-content-muted">Play Console</span>
              {consoleVersionQ.isLoading ? (
                <span className="text-content-subtle">Reading version…</span>
              ) : consoleVersionQ.error ? (
                <>
                  <span className="text-red-500">
                    {(consoleVersionQ.error as Error).message || 'Could not read version'}
                  </span>
                  <button type="button" onClick={() => consoleVersionQ.refetch()} className="text-primary hover:underline">
                    Retry
                  </button>
                </>
              ) : !consoleVersion?.connected ? (
                <span className="text-yellow-500">This bundle is not connected on the Play tab.</span>
              ) : (
                <>
                  <span className="text-content-subtle">
                    Version name: <code className="text-content">{consoleVersion.version_name ?? '—'}</code>
                  </span>
                  <span className="text-content-subtle">
                    Next version code: <code className="text-content">{consoleVersion.next_version_code ?? '—'}</code>
                    {consoleVersion.current_version_code != null ? ` (latest used: ${consoleVersion.current_version_code})` : ' (new app)'}
                  </span>
                  {consoleVersion.account_name && (
                    <span className="text-content-subtle">Account: {consoleVersion.account_name}</span>
                  )}
                  <button type="button" onClick={() => consoleVersionQ.refetch()} className="ml-auto text-primary hover:underline">
                    Refresh
                  </button>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 min-w-[12rem] flex-1">
                <span className="text-content-subtle text-xs">Base repository</span>
                <select
                  value={baseRepoId}
                  onChange={(e) => selectBaseRepo(e.target.value)}
                  className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
                >
                  <option value="">— select —</option>
                  {enabledRepos.map((r) => (
                    <option key={r.id} value={r.id}>{r.name} · {typeName(r.type_id) ?? 'no type'}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 min-w-[8rem]">
                <span className="text-content-subtle text-xs" title="The numbers the version style writes the name around">Version numbers</span>
                <input value={versionName} onChange={(e) => setVersionName(e.target.value)} className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
              </label>
              <label className="flex flex-col gap-1 min-w-[8rem]">
                <span className="text-content-subtle text-xs">Next version code</span>
                <input type="number" value={versionCode} onChange={(e) => setVersionCode(e.target.value)} className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
              </label>
              <button
                type="button"
                disabled={saveCfg.isPending}
                onClick={saveGeneral}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
              >
                {saveCfg.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 min-w-[12rem]">
                <span className="text-content-subtle text-xs">Version style</span>
                <select
                  value={versionScheme}
                  onChange={(e) => setVersionScheme(e.target.value)}
                  className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
                >
                  {!cfg?.version_scheme && <option value="">Random, drawn at the first build</option>}
                  {(cfgQ.data?.version_schemes ?? []).map((s) => (
                    <option key={s.id} value={s.id}>{s.example}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 min-w-[10rem]">
                <span className="text-content-subtle text-xs">Code grows per build by</span>
                <select
                  value={codeStepMax}
                  onChange={(e) => setCodeStepMax(e.target.value)}
                  className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
                >
                  {cfg?.version_code_step == null && <option value="">Random, drawn at the first build</option>}
                  {[1, ...(cfgQ.data?.version_code_steps ?? [])].map((n) => (
                    <option key={n} value={String(n)}>{n === 1 ? 'exactly 1' : `1 to ${n}`}</option>
                  ))}
                </select>
              </label>
              <p className="text-xs text-content-subtle pb-2">
                {cfg?.version_preview
                  ? <>Next build: <code className="text-content">{cfg.version_preview}</code> ({cfg.next_version_code})</>
                  : 'A new app also starts on random numbers and a random first code.'}
              </p>
            </div>
            {selectedRepoId != null && (() => {
              const t = typeName(enabledRepos.find((r) => r.id === selectedRepoId)?.type_id ?? null);
              return t ? (
                <p className="text-xs text-content-subtle">
                  Repo type: <span className="font-medium text-content">{t}</span> · rules are set on the{' '}
                  {onOpenRepos ? <button type="button" onClick={onOpenRepos} className="text-primary hover:underline">Repos Setup</button> : 'Repos Setup'} tab.
                </p>
              ) : (
                <p className="text-xs text-yellow-500">This repo has no type yet. Set it on the Repos Setup tab.</p>
              );
            })()}
            {latestRepoBuild?.base_repo_name && (
              <p className="text-xs text-content-subtle">
                Last build used <span className="font-medium text-content-muted">{latestRepoBuild.base_repo_name}</span>
                {latestRepoBuild.base_repo_owner && latestRepoBuild.base_repo_repo
                  ? ` · ${latestRepoBuild.base_repo_owner}/${latestRepoBuild.base_repo_repo}${latestRepoBuild.base_repo_branch ? `@${latestRepoBuild.base_repo_branch}` : ''}`
                  : ''}.
              </p>
            )}
            {repoMismatch && (
              <p className="text-sm text-yellow-500">
                ⚠ This differs from the repository used by the last build.
              </p>
            )}
            {configNeedsSave && (
              <p className="text-xs text-yellow-500">Save the configuration before starting a build.</p>
            )}
            {consoleCodeConflict && (
              <p className="text-sm text-red-500">
                Version code {versionCode} is already used in Play Console. Use {consoleVersion!.next_version_code} or higher.
              </p>
            )}
            {saveCfg.error && <p className="text-red-500 text-sm">{(saveCfg.error as Error).message}</p>}
          </section>

          {/* Repository history */}
          <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
            <h3 className="font-medium text-content">Base repository history</h3>
            {repoHistoryQ.isLoading ? (
              <p className="text-content-subtle text-sm">Loading…</p>
            ) : (repoHistoryQ.data?.length ?? 0) === 0 ? (
              <p className="text-content-subtle text-sm">No repository history yet. Legacy builds did not record their source repository.</p>
            ) : (
              <div className="space-y-2">
                {repoHistoryQ.data!.map((entry) => (
                  <div
                    key={`${entry.owner ?? ''}/${entry.repo ?? ''}@${entry.branch ?? ''}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface-muted px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="text-content text-sm truncate">{entry.name}</div>
                      {entry.owner && entry.repo && (
                        <div className="text-content-subtle text-xs truncate">
                          {entry.owner}/{entry.repo}{entry.branch ? `@${entry.branch}` : ''}
                        </div>
                      )}
                    </div>
                    <div className="text-content-subtle text-xs whitespace-nowrap">
                      {entry.build_count} build{entry.build_count === 1 ? '' : 's'} · last {fmtUnix(entry.last_used_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Keystore */}
          <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
            <h3 className="font-medium text-content">Keystore</h3>
            {cfg?.has_keystore ? (
              <div className="space-y-2">
                <p className="text-content-muted text-sm">Source: {cfg.keystore_source ?? 'unknown'}{cfg.keyalias ? ` · alias ${cfg.keyalias}` : ''}</p>
                <KeystoreFingerprint appId={appId} />
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={handleDownloadKeystore} className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted">
                    Download
                  </button>
                  <button
                    type="button"
                    disabled={showSigning.isPending}
                    onClick={() => (signing ? setSigning(null) : showSigning.mutate())}
                    className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
                  >
                    {signing ? 'Hide passwords' : (showSigning.isPending ? 'Loading…' : 'Show passwords')}
                  </button>
                  <button
                    type="button"
                    disabled={genKs.isPending}
                    onClick={regenerate}
                    className="px-3 py-1.5 text-sm rounded-md border border-red-500/40 text-red-500 hover:bg-red-500/10 disabled:opacity-40"
                  >
                    Regenerate
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReupload((v) => !v)}
                    className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted"
                  >
                    Upload own keystore
                  </button>
                </div>
                {showReupload && (
                  <div className="pt-2 border-t border-border/50 space-y-2">
                    <p className="text-red-500 text-xs">Warning: this replaces the current signing key.</p>
                    <div className="flex flex-wrap items-end gap-2">
                      <input
                        type="file"
                        accept=".keystore,.jks"
                        onChange={(e) => setKeystoreFile(e.target.files?.[0] ?? null)}
                        className="text-sm text-content"
                      />
                      <label className="flex flex-col gap-1 min-w-[8rem]">
                        <span className="text-content-subtle text-xs">Key alias</span>
                        <input value={keyalias} onChange={(e) => setKeyalias(e.target.value)} className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
                      </label>
                      <label className="flex flex-col gap-1 min-w-[8rem]">
                        <span className="text-content-subtle text-xs">Keystore pass</span>
                        <input type="password" value={keystorePass} onChange={(e) => setKeystorePass(e.target.value)} className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
                      </label>
                      <label className="flex flex-col gap-1 min-w-[8rem]">
                        <span className="text-content-subtle text-xs">Key pass</span>
                        <input type="password" value={keyaliasPass} onChange={(e) => setKeyaliasPass(e.target.value)} className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
                      </label>
                      <button
                        type="button"
                        disabled={!keystoreFile || saveCfg.isPending}
                        onClick={reuploadKeystore}
                        className="px-3 py-1.5 text-sm rounded-md border border-red-500/40 text-red-500 hover:bg-red-500/10 disabled:opacity-40"
                      >
                        {saveCfg.isPending ? 'Uploading…' : 'Upload'}
                      </button>
                    </div>
                  </div>
                )}
                {signing && (
                  <div className="bg-surface-muted rounded-md p-3 text-sm space-y-1">
                    <div className="text-content-subtle">Alias: <code className="text-content">{signing.keyalias ?? '—'}</code></div>
                    <div className="text-content-subtle">Keystore pass: <code className="text-content">{signing.keystore_pass}</code></div>
                    <div className="text-content-subtle">Key pass: <code className="text-content">{signing.keyalias_pass ?? '—'}</code></div>
                  </div>
                )}
                {genKs.error && <p className="text-red-500 text-sm">{(genKs.error as ApiError).message}</p>}
                {showSigning.error && <p className="text-red-500 text-sm">{(showSigning.error as ApiError).message}</p>}
                {ksDownloadError && <p className="text-red-500 text-sm">{ksDownloadError}</p>}
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  type="button"
                  disabled={genKs.isPending}
                  onClick={() => genKs.mutate(false)}
                  className="px-3 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
                >
                  {genKs.isPending ? 'Generating…' : 'Generate keystore'}
                </button>
                {genKs.error && <p className="text-red-500 text-sm">{(genKs.error as ApiError).message}</p>}

                <div className="pt-2 border-t border-border/50 space-y-2">
                  <p className="text-content-subtle text-xs">Or upload an existing .keystore/.jks:</p>
                  <div className="flex flex-wrap items-end gap-2">
                    <input
                      type="file"
                      accept=".keystore,.jks"
                      onChange={(e) => setKeystoreFile(e.target.files?.[0] ?? null)}
                      className="text-sm text-content"
                    />
                    <label className="flex flex-col gap-1 min-w-[8rem]">
                      <span className="text-content-subtle text-xs">Key alias</span>
                      <input value={keyalias} onChange={(e) => setKeyalias(e.target.value)} className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
                    </label>
                    <label className="flex flex-col gap-1 min-w-[8rem]">
                      <span className="text-content-subtle text-xs">Keystore pass</span>
                      <input type="password" value={keystorePass} onChange={(e) => setKeystorePass(e.target.value)} className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
                    </label>
                    <label className="flex flex-col gap-1 min-w-[8rem]">
                      <span className="text-content-subtle text-xs">Key pass</span>
                      <input type="password" value={keyaliasPass} onChange={(e) => setKeyaliasPass(e.target.value)} className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
                    </label>
                    <button
                      type="button"
                      disabled={!keystoreFile || saveCfg.isPending}
                      onClick={uploadKeystore}
                      className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
                    >
                      {saveCfg.isPending ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>

          <AppDomainPanel appId={appId} domainOff={repoType?.rules.domain === 'off'} />

          {repoType && (['loader_icon', 'loader_text', 'loader_bg'] as const).some((k) => repoType.rules[k] !== 'off') && (
            <LoaderPanel appId={appId} loader={cfgQ.data?.loader} rules={repoType.rules} />
          )}

          {repoType?.rules.unity_version !== 'off' && <UnityVersionPanel appId={appId} unity={cfgQ.data?.unity} />}

          {repoType?.rules.random_seed !== 'off' && <RandomSeedPanel appId={appId} status={cfgQ.data?.random_seed} />}

          {/* Build actions */}
          <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
            <h3 className="font-medium text-content">Build</h3>
            {!readiness?.ok ? (
              <p className="text-xs text-red-500">Build is off until Build readiness is all set.</p>
            ) : ads && !ads.enabled && repoType?.rules.admob !== 'off' && (
              <p className="text-xs text-yellow-500">Building without ads — AdMob is not connected for this app.</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={!readiness?.ok || configNeedsSave || consoleCodeConflict || start.isPending}
                onClick={() => start.mutate('apk')}
                className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
              >
                {start.isPending ? 'Starting…' : 'Build APK'}
              </button>
              <button
                type="button"
                disabled={!readiness?.ok || configNeedsSave || consoleCodeConflict || start.isPending}
                onClick={() => {
                  const check = aabCheckQ.data;
                  if (check && check.state !== 'safe'
                    && !confirm(`Build an AAB anyway?\n\n• ${aabReasons(check).join('\n• ')}`)) return;
                  start.mutate('aab');
                }}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md hover:opacity-90 disabled:opacity-40 ${aabCheckQ.data ? AAB_BUTTON[aabCheckQ.data.state] : 'bg-primary text-white'}`}
              >
                {aabCheckQ.data && <AabStateIcon state={aabCheckQ.data.state} />}
                {start.isPending ? 'Starting…' : 'Build AAB'}
              </button>
              {cfgQ.data?.unity?.version && repoType?.rules.unity_version !== 'off' && (
                <span className="text-content-subtle text-xs">Builds with Unity {cfgQ.data.unity.version}</span>
              )}
            </div>
            {start.error && <p className="text-red-500 text-sm">{(start.error as ApiError).message}</p>}
            <AabCheckPanel check={aabCheckQ.data} loading={aabCheckQ.isLoading && buildsQ.isSuccess} />
          </section>

          {/* This app's builds */}
          <section className="bg-surface border border-border rounded-lg p-4">
            <h3 className="font-medium text-content mb-2">Builds of this app</h3>
            <BuildsTable
              builds={buildsQ.data ?? []}
              onQaSaved={() => {
                qc.invalidateQueries({ queryKey: ['app-builds', appId] });
                qc.invalidateQueries({ queryKey: ['app-builds-recent'] });
              }}
            />
          </section>

          {/* Handing the app out lives on its own tab — the ASO who makes the
              listing and the frames has no access to this one. */}
          <section className="bg-surface border border-border rounded-lg p-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-medium text-content">Hand this app over</h3>
              <p className="mt-1 text-sm text-content-subtle">
                Listing, icon, screenshots and one signed build behind an expiring link — on the Share tab.
              </p>
            </div>
            <button
              type="button"
              onClick={onOpenShare}
              className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:opacity-90"
            >
              Open Share
            </button>
          </section>
        </>
      )}
    </div>
  );
}

export default function BuildTab({ onOpenShare, onOpenRepos }: { onOpenShare: () => void; onOpenRepos?: () => void }) {
  const qc = useQueryClient();
  const appsQ = useQuery({ queryKey: ['apps'], queryFn: getApps });
  const recentQ = useQuery({
    queryKey: ['app-builds-recent'],
    queryFn: () => getBuilds(),
    refetchInterval: (q) => ((q.state.data ?? []) as AppBuild[]).some((b) => ACTIVE.has(b.status)) ? 10_000 : false,
  });
  // Pre-selected by the ?app= deep link (Update Batches → APK/AAB cells).
  const [searchParams] = useSearchParams();
  const [appId, setAppId] = useState(searchParams.get('app') ?? '');
  const [recentPage, setRecentPage] = useState(1);
  const androidApps = useMemo(
    () => (appsQ.data ?? []).filter((a) => a.platform === 'android'),
    [appsQ.data],
  );
  const appOptions = useMemo(
    () => androidApps.map((app) => ({
      value: app.id,
      label: `${app.name} · ${app.bundle_id}`,
    })),
    [androidApps],
  );
  const recentBuilds = recentQ.data ?? [];
  const recentPageCount = Math.max(1, Math.ceil(recentBuilds.length / RECENT_BUILDS_PAGE_SIZE));
  const currentRecentPage = Math.min(recentPage, recentPageCount);
  const paginatedRecentBuilds = recentBuilds.slice(
    (currentRecentPage - 1) * RECENT_BUILDS_PAGE_SIZE,
    currentRecentPage * RECENT_BUILDS_PAGE_SIZE,
  );

  useEffect(() => {
    setRecentPage((current) => Math.min(current, recentPageCount));
  }, [recentPageCount]);

  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
      <main className="min-w-0 space-y-4">
        <section className="bg-surface border border-border rounded-lg p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-lg font-medium text-content">Build an app</h2>
              <p className="mt-1 text-sm text-content-subtle">
                Select an Android app, review its configuration, and start a build.
              </p>
            </div>
            <Select
              value={appId}
              options={appOptions}
              onChange={setAppId}
              placeholder={appsQ.isLoading ? 'Loading…' : '— select an app —'}
              searchable
              disabled={appsQ.isLoading}
              className="w-full sm:w-[24rem]"
            />
          </div>
        </section>

        {appId ? (
          <AppBuildPanel key={appId} appId={appId} onOpenShare={onOpenShare} onOpenRepos={onOpenRepos} />
        ) : (
          <section className="flex min-h-64 items-center justify-center rounded-lg border border-dashed border-border bg-surface/40 p-8 text-center">
            <div className="max-w-sm">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6v12m6-6H6" />
                </svg>
              </div>
              <p className="font-medium text-content">Choose an app to begin</p>
              <p className="mt-1 text-sm text-content-subtle">
                Its readiness, repository, version, signing key, and build controls will appear here.
              </p>
            </div>
          </section>
        )}
      </main>

      <aside className="min-w-0 space-y-4 xl:sticky xl:top-4">
        <RecentBuildsPanel
          builds={paginatedRecentBuilds}
          loading={recentQ.isLoading}
          page={currentRecentPage}
          pageCount={recentPageCount}
          onPrevious={() => setRecentPage((current) => Math.max(1, current - 1))}
          onNext={() => setRecentPage((current) => Math.min(recentPageCount, current + 1))}
          onQaSaved={() => {
            qc.invalidateQueries({ queryKey: ['app-builds-recent'] });
            if (appId) qc.invalidateQueries({ queryKey: ['app-builds', appId] });
          }}
        />
        {onOpenRepos && (
          <p className="text-xs text-content-subtle px-1">
            Base repositories and what each type of repo needs live on the{' '}
            <button type="button" onClick={onOpenRepos} className="text-primary hover:underline">Repos Setup</button> tab.
          </p>
        )}
      </aside>
    </div>
  );
}
